import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { getModalPortalTarget } from '../lib/portalTarget';

interface Props {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Bestaetigung als Warnung (rot) statt Standard (gruen). */
  danger?: boolean;
  busy?: boolean;
  /**
   * top = über anderen Dialogen (Rückfrage „Änderungen verwerfen?" aus einem offenen
   * Dialog heraus). Standard dialog = z-50 wie alle Dialoge.
   */
  layer?: 'dialog' | 'top';
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Generische Bestaetigungs-Modal-Komponente (verlagert 2026-08-02 aus
 * bauberichteV2/components/ nach @onvice/portal-ui). Bereits konsumiert
 * von bauberichteV2 selbst + Portal-Shell-Admin-Pages. createPortal in
 * document.body, damit transformed Ancestor (z.B. eingebettete Widgets)
 * `position: fixed` nicht in den Container einsperrt.
 */
export default function ConfirmModal({
  open, title, message,
  confirmLabel = 'OK', cancelLabel = 'Abbrechen',
  danger = false, busy = false, layer = 'dialog',
  onClose, onConfirm,
}: Props) {
  if (!open) return null;
  const confirmCls = danger
    ? 'bg-rose-600 hover:bg-rose-500 text-white'
    : 'bg-brand-600 hover:bg-brand-500 text-primary-foreground';

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      style={layer === 'top' ? { zIndex: 70 } : undefined}
      onClick={() => !busy && onClose()}
    >
      <div className="bg-slate-900 border border-slate-800 rounded-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h3 className="font-semibold text-slate-100">{title}</h3>
          <button
            onClick={() => !busy && onClose()}
            disabled={busy}
            className="text-slate-400 hover:text-slate-200 min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4">
          <p className="text-sm text-slate-300 leading-relaxed">{message}</p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 border-t border-slate-800">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-3 sm:py-2 rounded text-slate-300 hover:bg-slate-800 disabled:opacity-50 min-h-[44px]"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-3 sm:py-2 rounded inline-flex items-center justify-center gap-2 disabled:opacity-50 min-h-[44px] ${confirmCls}`}
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    getModalPortalTarget(),
  );
}
